/**
 * Presentational file attachment picker for forms.
 * Displays uploaded + pending files with an "Add file" button.
 * Pair with a domain-specific hook (e.g. `useFileAttachments`) that
 * manages upload logic and provides the props.
 */
import { useRef } from 'react'

import { ALLOWED_CONTENT_TYPES } from '@snootsvet/shared'

import { FormLabel } from '@/frontend/components/form/primitives'
import { Alert, AlertDescription } from '@/frontend/components/ui/alert'
import { Button } from '@/frontend/components/ui/button'

/** An existing file already persisted on the server. */
export interface ExistingFile {
  id: string
  fileName: string
}

export interface FileAttachmentProps {
  pendingFiles: File[]
  uploadedKeys: { s3Key: string; fileName: string }[]
  uploading: boolean
  onAdd: (files: File[]) => void
  /** Remove a pending (not yet uploaded) file. */
  onRemovePending: (file: File) => void
  /** Existing server-side files (edit mode). */
  existingFiles?: ExistingFile[]
  /** IDs of existing files marked for removal. */
  removedFileIds?: Set<string>
  /** Mark an existing file for removal on submit. */
  onRemoveExisting?: (id: string) => void
  /** Undo a pending removal of an existing file. */
  onRestoreExisting?: (id: string) => void
  required?: boolean
  fileCountError?: string
}

/** Displays uploaded + pending files and an "Add file" button. */
export function FileAttachments({
  pendingFiles,
  uploadedKeys,
  uploading,
  onAdd,
  onRemovePending,
  existingFiles,
  removedFileIds,
  onRemoveExisting,
  onRestoreExisting,
  required,
  fileCountError,
}: FileAttachmentProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)

  return (
    <div className="flex flex-col gap-1.5">
      <FormLabel required={required}>Attachments</FormLabel>
      {existingFiles?.map((file) => {
        const removed = removedFileIds?.has(file.id)
        return (
          <div key={file.id} className="flex items-center gap-2">
            <span
              className={`size-2 shrink-0 rounded-full ${removed ? 'bg-warning' : 'bg-success'}`}
            />
            <span className={removed ? 'line-through text-muted-foreground' : ''}>
              {file.fileName}
            </span>
            {!removed && onRemoveExisting && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => onRemoveExisting(file.id)}
                aria-label={`Remove ${file.fileName}`}
              >
                Remove
              </Button>
            )}
            {removed && onRestoreExisting && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => onRestoreExisting(file.id)}
                aria-label={`Restore ${file.fileName}`}
              >
                Restore
              </Button>
            )}
          </div>
        )
      })}
      {uploadedKeys.map((key) => (
        <div key={key.s3Key} className="text-muted-foreground">
          {key.fileName}
        </div>
      ))}
      {pendingFiles.map((file) => (
        <div
          key={`${file.name}-${file.size}-${file.lastModified}`}
          className="flex items-center gap-2"
        >
          <span className="size-2 shrink-0 rounded-full bg-warning" />
          <span>{file.name}</span>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onRemovePending(file)}
            aria-label={`Remove ${file.name}`}
          >
            Remove
          </Button>
        </div>
      ))}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={ALLOWED_CONTENT_TYPES.join(',')}
        className="hidden"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? [])
          onAdd(files)
          e.target.value = ''
        }}
      />
      {fileCountError && (
        <Alert variant="destructive">
          <AlertDescription>{fileCountError}</AlertDescription>
        </Alert>
      )}
      <Button
        type="button"
        variant="outline"
        className="w-fit"
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading || !!fileCountError}
      >
        {uploading ? 'Uploading...' : 'Add file'}
      </Button>
    </div>
  )
}
